/**
 * Loyalty points-only lines in the cart drawer — points-balance cap.
 *
 * Points-only lines use the theme's native stepper (cart.js: debounce + optimistic
 * update; for these lines it sends /cart/update.js with the synced loyalty
 * free-items map). This script only:
 *   - caps each points line's max quantity at what the points balance allows,
 *     counting every points-only line in the cart (cart.js then disables "+"),
 *   - drops free-items map entries for variants no longer in the cart (safety net).
 * The balance starts from [data-fpo-cart-state][data-fpo-balance] (customer
 * metafield rendered by the theme) and is refined once from the app proxy.
 */
(function () {
  var PROXY = "/apps/loyalty";

  function numericId(gid) { return String(gid || "").split("/").pop(); }
  function stateEl() { return document.querySelector("[data-fpo-cart-state]"); }

  /* --- points balance --------------------------------------------------- */

  var balance = null;
  var balancePromise = null;

  function liquidBalance() {
    var s = stateEl();
    var v = s ? parseInt(s.getAttribute("data-fpo-balance"), 10) : NaN;
    return Number.isFinite(v) ? v : null;
  }
  function knownBalance() { return balance != null ? balance : liquidBalance(); }
  function isLoggedIn() { var s = stateEl(); return !!(s && s.hasAttribute("data-fpo-balance")); }

  function fetchBalance() {
    if (!balancePromise) {
      var url = PROXY + "/customer";
      var req = window.LoyaltyCache
        ? window.LoyaltyCache.get(url)
        : fetch(url, { headers: { Accept: "application/json" } }).then(function (r) { return r.json(); });
      balancePromise = Promise.resolve(req)
        .then(function (d) {
          var b = d && d.customer && d.customer.currentBalance;
          if (typeof b === "number") balance = b;
          applyLimits();
          return knownBalance();
        })
        .catch(function () { return knownBalance(); });
    }
    return balancePromise;
  }

  /* --- cap -------------------------------------------------------------- */

  function lineQty(li) {
    var input = li.querySelector("[data-cart-qty-input]");
    var v = parseInt(input ? input.value : li.getAttribute("data-line-quantity"), 10);
    return Number.isFinite(v) && v > 0 ? v : 0;
  }

  function applyLimits() {
    var lines = Array.prototype.slice.call(document.querySelectorAll("[data-cart-line][data-fpo-points-cost]"));
    if (!lines.length) return;
    var bal = knownBalance();
    if (bal == null) return;
    var spent = lines.reduce(function (s, li) {
      return s + lineQty(li) * (parseInt(li.getAttribute("data-fpo-points-cost"), 10) || 0);
    }, 0);
    lines.forEach(function (li) {
      var cost = parseInt(li.getAttribute("data-fpo-points-cost"), 10) || 0;
      if (!cost) return;
      var qty = lineQty(li);
      var max = qty + Math.floor(Math.max(bal - spent, 0) / cost);
      var stock = parseInt(li.getAttribute("data-fpo-stock-max"), 10);
      if (Number.isFinite(stock) && stock > 0) max = Math.min(max, stock);
      max = Math.max(max, qty, 1);
      var input = li.querySelector("[data-cart-qty-input]");
      if (input) input.max = String(max);
      li.dataset.cartMaxQuantity = String(max);
      var plus = li.querySelector("[data-cart-qty-plus]");
      if (plus) plus.disabled = qty >= max;
    });
  }

  function scheduleLimits() { window.requestAnimationFrame(applyLimits); }

  // After the theme's own +/-/typing handlers ran (they update the input first).
  document.addEventListener("click", function (e) {
    if (e.target.closest && e.target.closest("[data-cart-line][data-fpo-points-cost]")) scheduleLimits();
  });
  document.addEventListener("input", scheduleLimits);
  document.addEventListener("change", scheduleLimits);

  /* --- free-items map safety net ----------------------------------------- */

  // Quantity edits already carry the map (cart.js). Only clean up entries whose
  // variant left the cart entirely, so a stale entry can't over-deduct points.
  var reconciling = false;
  function reconcile() {
    if (reconciling) return;
    var s = stateEl();
    if (!s) return;
    var map;
    try { map = JSON.parse(s.getAttribute("data-fpo-free-items") || "{}") || {}; } catch (e) { return; }
    var present = {};
    document.querySelectorAll("[data-cart-line][data-variant-id]").forEach(function (li) {
      present[numericId(li.getAttribute("data-variant-id"))] = true;
    });
    var changed = false;
    Object.keys(map).forEach(function (gid) {
      if (!present[numericId(gid)]) { delete map[gid]; changed = true; }
    });
    if (!changed) return;
    var total = Object.keys(map).reduce(function (sum, k) { return sum + ((map[k] && map[k].spent) || 0); }, 0);
    reconciling = true;
    s.setAttribute("data-fpo-free-items", JSON.stringify(map));
    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: { _loyalty_free_items: JSON.stringify(map), _loyalty_points_spent: String(total) } }),
    }).catch(function () {}).then(function () { reconciling = false; });
  }

  function onRendered() {
    applyLimits();
    reconcile();
    if (isLoggedIn() && document.querySelector("[data-cart-line][data-fpo-points-cost], [data-fpo]")) fetchBalance();
  }

  document.addEventListener("fwn:cart-rendered", onRendered);
  if (document.readyState !== "loading") onRendered();
  else document.addEventListener("DOMContentLoaded", onRendered);
})();
